import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { api, calcLive, formatApiErrorDetail } from "../lib/api";
import { useAuth } from "../lib/AuthContext";
import StatsCards from "./StatsCards";
import TradesTable from "./TradesTable";
import TradeDialog from "./TradeDialog";
import JournalDialog from "./JournalDialog";
import CoachInsights from "./CoachInsights";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Plus, RefreshCw, Download, Upload, LogOut, Search, BarChart3, X, KeyRound, UserCircle2 } from "lucide-react";
import { toast } from "sonner";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "../components/ui/alert-dialog";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from "../components/ui/dropdown-menu";
import ChangePasswordDialog from "./ChangePasswordDialog";

const ALL = "__all__";
const SORT_OPTIONS = [
  { v: "date_desc", label: "Newest first" },
  { v: "date_asc", label: "Oldest first" },
  { v: "pl_desc", label: "P&L: High → Low" },
  { v: "pl_asc", label: "P&L: Low → High" },
  { v: "broker_asc", label: "Broker A → Z" },
];

export default function Dashboard() {
  const { user, logout } = useAuth();
  const [trades, setTrades] = useState([]);
  const [stats, setStats] = useState(null);
  const [reference, setReference] = useState(null);
  const [prices, setPrices] = useState({});
  const [filters, setFilters] = useState({ broker: ALL, category: ALL, status: ALL, search: "" });
  const [sortBy, setSortBy] = useState("date_desc");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [journalTrade, setJournalTrade] = useState(null);
  const [confirmDel, setConfirmDel] = useState(null);
  const [pwOpen, setPwOpen] = useState(false);
  const [pricesFetching, setPricesFetching] = useState(false);
  const [lastPriceFetch, setLastPriceFetch] = useState(null);
  const fileInputRef = useRef(null);

  // "Latest-wins" guard: each loadAll() increments a request-id; only the newest
  // response's data is applied, so out-of-order fetches don't leave stale rows.
  const loadReqId = useRef(0);
  const loadAll = useCallback(async () => {
    const reqId = ++loadReqId.current;
    try {
      const params = {};
      if (filters.broker !== ALL) params.broker = filters.broker;
      if (filters.category !== ALL) params.category = filters.category;
      if (filters.status !== ALL) params.status = filters.status;
      if (filters.search) params.search = filters.search;
      const [tRes, sRes] = await Promise.all([
        api.get("/trades", { params }),
        api.get("/stats/summary"),
      ]);
      if (reqId !== loadReqId.current) return; // superseded — ignore
      setTrades(tRes.data);
      setStats(sRes.data);
    } catch (e) {
      if (reqId !== loadReqId.current) return;
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to load");
    }
  }, [filters]);

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get("/reference");
        setReference(r.data);
      } catch (err) {
        console.warn("Failed to load reference data:", err);
      }
    })();
  }, []);

  // Debounce search input (300ms) — non-search filters apply instantly
  useEffect(() => {
    if (!filters.search) {
      loadAll();
      return;
    }
    const t = setTimeout(() => loadAll(), 300);
    return () => clearTimeout(t);
  }, [loadAll, filters.search]);

  const openSymbols = useMemo(() => {
    const map = new Map();
    trades.forEach((t) => {
      if (t.stock) {
        const key = t.stock.toUpperCase();
        if (!map.has(key)) map.set(key, { symbol: key, exchange: t.exchange || "NS" });
      }
    });
    return Array.from(map.values());
  }, [trades]);

  const fetchPrices = useCallback(async (force = false) => {
    if (openSymbols.length === 0) return;
    setPricesFetching(true);
    try {
      const endpoint = force ? "/prices/refresh" : "/prices/batch";
      // Client-side chunking as a safety net (backend caps at 500 per call).
      const CHUNK = 400;
      const merged = {};
      for (let i = 0; i < openSymbols.length; i += CHUNK) {
        const chunk = openSymbols.slice(i, i + CHUNK);
        const { data } = await api.post(endpoint, { symbols: chunk });
        Object.assign(merged, data);
      }
      setPrices(merged);
      setLastPriceFetch(new Date());
    } catch (err) {
      console.warn("Price batch fetch failed:", err.message);
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed to refresh LTPs");
    } finally {
      setPricesFetching(false);
    }
  }, [openSymbols]);

  // Auto-load once when the set of tracked symbols changes; served from daily cache when available.
  const symbolsKey = JSON.stringify(openSymbols);
  useEffect(() => {
    if (openSymbols.length > 0) fetchPrices(false);
  }, [symbolsKey]);
  // NOTE: 60-second auto-refresh removed. LTPs are now cached daily.
  // Users click the Refresh button to force-refresh (rate-limited: 20/hr).

  // Cache date from any successful price entry (all entries share the same cache_date within a batch)
  const cacheDate = useMemo(() => {
    for (const v of Object.values(prices || {})) {
      if (v && v.cache_date) return v.cache_date;
    }
    return null;
  }, [prices]);

  const totalLivePL = useMemo(() => {
    let sum = 0;
    trades.forEach((t) => {
      if (t.status === "CLOSED") return;
      const p = prices?.[t.stock?.toUpperCase()];
      if (p?.last) {
        const live = calcLive(t, p.last);
        if (live.livePL != null) sum += live.livePL;
      }
    });
    return sum;
  }, [trades, prices]);

  // Compute effective P&L per trade (realized for CLOSED, live for HOLDING)
  function tradePL(t) {
    if (t.status === "CLOSED" && t.exit_rate != null) {
      const sign = t.transaction === "BOUGHT" ? 1 : -1;
      return sign * (t.exit_rate - t.rate) * t.quantity;
    }
    const p = prices?.[t.stock?.toUpperCase()];
    if (p?.last) {
      const live = calcLive(t, p.last);
      return live.livePL ?? 0;
    }
    return 0;
  }

  const sortedTrades = useMemo(() => {
    const rows = [...trades];
    const dateKey = (t) => t.exit_date || t.date_of_purchase || "";
    switch (sortBy) {
      case "date_asc":
        rows.sort((a, b) => dateKey(a).localeCompare(dateKey(b)) || (a.created_at || "").localeCompare(b.created_at || ""));
        break;
      case "pl_desc":
        rows.sort((a, b) => tradePL(b) - tradePL(a));
        break;
      case "pl_asc":
        rows.sort((a, b) => tradePL(a) - tradePL(b));
        break;
      case "broker_asc":
        rows.sort((a, b) => (a.broker || "").localeCompare(b.broker || "") || dateKey(b).localeCompare(dateKey(a)));
        break;
      case "date_desc":
      default:
        // Server already returns date_desc — no-op, preserves order
        break;
    }
    return rows;
  }, [trades, prices, sortBy]);

  function openNew() { setEditing(null); setDialogOpen(true); }
  function openEdit(t) { setEditing(t); setDialogOpen(true); }

  // Called every time a trade is added/edited — reload trades AND refresh live LTP
  const onTradeSaved = useCallback(async () => {
    await loadAll();
    // Force price refresh (bypass the openSymbols-diff memoization so LTP is always current)
    fetchPrices();
  }, [loadAll, fetchPrices]);

  async function doDelete() {
    if (!confirmDel) return;
    try {
      await api.delete(`/trades/${confirmDel.id}`);
      toast.success("Trade deleted");
      setConfirmDel(null);
      loadAll();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to delete");
    }
  }

  async function doExport() {
    try {
      const res = await api.get("/trades/export", { responseType: "blob" });
      const blob = new Blob([res.data], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `trades_${new Date().toISOString().slice(0,10)}.xlsx`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error("Export failed");
    }
  }

  async function doImport(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/trades/import", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`Imported ${data.imported} trades${data.errors.length ? ` (${data.errors.length} errors)` : ""}`);
      loadAll();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Import failed");
    } finally {
      e.target.value = "";
    }
  }

  const fmtTime = (d) => d ? d.toLocaleTimeString("en-IN", { hour12: false }) : "—";

  return (
    <div className="min-h-screen bg-[#08080A] text-white" data-testid="dashboard">
      {/* Top bar */}
      <header className="border-b border-[#27272A] bg-[#08080A] sticky top-0 z-20">
        <div className="px-4 sm:px-6 py-3 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="h-7 w-7 bg-[#007AFF] flex items-center justify-center rounded-sm">
              <BarChart3 className="h-3.5 w-3.5 text-white" />
            </div>
            <div>
              <div className="text-xs tracking-[0.25em] uppercase font-semibold">Trade Monitor</div>
              <div className="text-[10px] text-[#71717A] num">
                LTPs · {cacheDate ? `cached ${cacheDate}` : lastPriceFetch ? fmtTime(lastPriceFetch) : "not fetched"} {pricesFetching && "· updating…"}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button data-testid="refresh-prices" variant="outline" size="sm" onClick={() => fetchPrices(true)} disabled={pricesFetching}
              className="bg-transparent border-[#27272A] text-[#A1A1AA] hover:bg-[#1A1B23] hover:text-white rounded-sm">
              <RefreshCw className={`h-3.5 w-3.5 ${pricesFetching ? "animate-spin" : ""}`} />
              <span className="hidden sm:inline ml-2 text-xs">Refresh LTPs</span>
            </Button>
            <div className="hidden sm:block text-xs text-[#A1A1AA] px-3 border-l border-[#27272A]">
              {user?.email}
            </div>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button data-testid="user-menu-btn" variant="ghost" size="sm"
                  className="text-[#A1A1AA] hover:text-white hover:bg-[#1A1B23] rounded-sm">
                  <UserCircle2 className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="bg-[#121318] border-[#27272A] text-white rounded-sm min-w-[180px]" data-testid="user-menu">
                <DropdownMenuLabel className="text-[10px] tracking-[0.2em] uppercase text-[#71717A] font-normal">
                  {user?.email}
                </DropdownMenuLabel>
                <DropdownMenuSeparator className="bg-[#27272A]" />
                <DropdownMenuItem
                  data-testid="menu-change-password"
                  onSelect={() => setPwOpen(true)}
                  className="cursor-pointer text-xs focus:bg-[#1A1B23] focus:text-white"
                >
                  <KeyRound className="h-3.5 w-3.5 mr-2" /> Change Password
                </DropdownMenuItem>
                <DropdownMenuSeparator className="bg-[#27272A]" />
                <DropdownMenuItem
                  data-testid="menu-logout"
                  onSelect={logout}
                  className="cursor-pointer text-xs text-[#FF3B30] focus:bg-[#FF3B30]/10 focus:text-[#FF3B30]"
                >
                  <LogOut className="h-3.5 w-3.5 mr-2" /> Sign Out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </header>

      <main className="px-4 sm:px-6 py-6 space-y-6 max-w-[1600px] mx-auto">
        <div className="flex items-end justify-between flex-wrap gap-4">
          <div>
            <div className="text-[10px] tracking-[0.3em] uppercase text-[#71717A] font-semibold">Portfolio</div>
            <h1 className="text-3xl sm:text-4xl font-semibold tracking-tight mt-1">Dashboard</h1>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <input ref={fileInputRef} type="file" accept=".xlsx,.xls,.csv" onChange={doImport} className="hidden" data-testid="import-input" />
            <Button data-testid="import-btn" variant="outline" onClick={() => fileInputRef.current?.click()}
              className="bg-transparent border-[#27272A] text-[#A1A1AA] hover:bg-[#1A1B23] hover:text-white rounded-sm">
              <Upload className="h-3.5 w-3.5 mr-2" /> <span className="text-xs">Import</span>
            </Button>
            <Button data-testid="export-btn" variant="outline" onClick={doExport}
              className="bg-transparent border-[#27272A] text-[#A1A1AA] hover:bg-[#1A1B23] hover:text-white rounded-sm">
              <Download className="h-3.5 w-3.5 mr-2" /> <span className="text-xs">Export</span>
            </Button>
            <Button data-testid="new-trade-btn" onClick={openNew}
              className="bg-[#007AFF] hover:bg-[#007AFF]/90 text-white rounded-sm">
              <Plus className="h-4 w-4 mr-2" /> New Trade
            </Button>
          </div>
        </div>

        <StatsCards stats={stats} totalLivePL={totalLivePL} />

        <CoachInsights />

        {/* Filters */}
        <div className="bg-[#121318] border border-[#27272A] p-3 flex flex-wrap items-center gap-2">
          <div className="relative flex-1 min-w-[200px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-[#71717A]" />
            <Input data-testid="filter-search" placeholder="Search stock…" value={filters.search}
              onChange={(e) => setFilters({ ...filters, search: e.target.value })}
              className="bg-[#08080A] border-[#27272A] text-white pl-9 rounded-sm uppercase placeholder:text-[#71717A] placeholder:normal-case" />
          </div>
          <FilterSelect label="Broker" value={filters.broker} options={reference?.brokers || []}
            onChange={(v) => setFilters({ ...filters, broker: v })} testid="filter-broker" />
          <FilterSelect label="Category" value={filters.category} options={reference?.categories || []}
            onChange={(v) => setFilters({ ...filters, category: v })} testid="filter-category" />
          <FilterSelect label="Status" value={filters.status} options={reference?.statuses || []}
            onChange={(v) => setFilters({ ...filters, status: v })} testid="filter-status" />
          <Select value={sortBy} onValueChange={setSortBy}>
            <SelectTrigger data-testid="sort-select" className="bg-[#08080A] border-[#27272A] text-white rounded-sm w-[180px] h-9 text-xs">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-[#121318] border-[#27272A] text-white rounded-sm">
              {SORT_OPTIONS.map((o) => (
                <SelectItem key={o.v} value={o.v} className="text-white focus:bg-[#1A1B23] text-xs" data-testid={`sort-${o.v}`}>
                  {o.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {(filters.broker !== ALL || filters.category !== ALL || filters.status !== ALL || filters.search) && (
            <Button data-testid="clear-filters" variant="ghost" size="sm"
              onClick={() => setFilters({ broker: ALL, category: ALL, status: ALL, search: "" })}
              className="text-[#71717A] hover:text-white hover:bg-[#1A1B23] rounded-sm">
              <X className="h-3 w-3 mr-1" /> Clear
            </Button>
          )}
        </div>

        <TradesTable trades={sortedTrades} prices={prices} onEdit={openEdit} onDelete={setConfirmDel} onJournal={setJournalTrade} />
      </main>

      <TradeDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        initial={editing}
        reference={reference}
        onSaved={onTradeSaved}
      />

      <JournalDialog
        open={!!journalTrade}
        onClose={() => setJournalTrade(null)}
        trade={journalTrade}
        onSaved={loadAll}
      />

      <AlertDialog open={!!confirmDel} onOpenChange={(o) => !o && setConfirmDel(null)}>
        <AlertDialogContent className="bg-[#121318] border-[#27272A] text-white rounded-sm" data-testid="delete-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-white">Delete this trade?</AlertDialogTitle>
            <AlertDialogDescription className="text-[#A1A1AA]">
              {confirmDel?.stock} · {confirmDel?.quantity} @ {confirmDel?.rate}. This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-transparent border-[#27272A] text-[#A1A1AA] hover:bg-[#1A1B23] hover:text-white rounded-sm">Cancel</AlertDialogCancel>
            <AlertDialogAction data-testid="confirm-delete" onClick={doDelete}
              className="bg-[#FF3B30] hover:bg-[#FF3B30]/90 text-white rounded-sm">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <ChangePasswordDialog open={pwOpen} onClose={() => setPwOpen(false)} />
    </div>
  );
}

function FilterSelect({ label, value, options, onChange, testid }) {
  return (
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger data-testid={testid} className="bg-[#08080A] border-[#27272A] text-white rounded-sm w-[160px] h-9 text-xs">
        <SelectValue placeholder={label} />
      </SelectTrigger>
      <SelectContent className="bg-[#121318] border-[#27272A] text-white rounded-sm">
        <SelectItem value={ALL} className="text-white focus:bg-[#1A1B23] text-xs">All {label === "Status" ? "Statuses" : label === "Category" ? "Categories" : `${label}s`}</SelectItem>
        {options.map((o) => <SelectItem key={o} value={o} className="text-white focus:bg-[#1A1B23] text-xs">{o}</SelectItem>)}
      </SelectContent>
    </Select>
  );
}
